/* =====================================================================
   TAB Calculator Library — SPIRITS data / config  ·  V2
   Ported verbatim from the approved prototype (calc-data.js: `spirits`,
   GROUPS.spirits, META incl. SPIRIT_CAUTION). 12 calculators only, in
   four groups:
     • Plan your wash  • Estimate and record  • Blend and dilute  • Bottle and serve
   The planning/record-keeping caution appears beneath Sugar Wash,
   Theoretical Spirit Yield and Spirit Cuts (engine renders it as a
   non-alarming "Planning & record keeping only" note).
   Loaded only on the Spirits page; registers with the shared engine.
   ===================================================================== */
(function () {
  'use strict';

  var num = function (x) { return parseFloat(x); };
  var has = function (x) { return x !== '' && x != null && !isNaN(parseFloat(x)); };
  var d = function (n, dp) { return Number(n).toFixed(dp); };

  var CAUTION = 'Use this calculator for planning and record keeping only. Distilling alcohol in Australia requires the appropriate excise manufacturer licence. Check your legal obligations before proceeding.';

  var spirits = [
    {
      id: 'spirits-dilution',
      name: 'Spirit Dilution Calculator',
      desc: 'Calculate water needed to reduce spirit to bottling strength.',
      group: 'blend', tag: 'Most used',
      need: 'Your current spirit volume, current ABV and target bottling strength.',
      accuracy: 'This is an estimate. Actual alcohol content may vary.',
      next: [{ t: 'spirits-bottle', label: 'Estimate bottle yield' }, { h: '/blogs/all/understanding-alcohol-dilution', label: 'Understanding alcohol dilution' }],
      fields: [
        { key: 'vol', label: 'Current spirit volume', type: 'number', dim: 'vol', default: '5' },
        { key: 'cabv', label: 'Current ABV', type: 'number', unit: '%', default: '70', step: '0.1' },
        { key: 'tabv', label: 'Target ABV', type: 'number', unit: '%', default: '40', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.cabv) || !has(v.tabv)) return null;
        if (num(v.vol) <= 0) return { error: 'Current volume must be greater than zero.' };
        if (num(v.tabv) >= num(v.cabv)) return { error: 'Target ABV must be lower than current ABV.' };
        var curL = s.toL(v.vol), waterL = curL * (num(v.cabv) / num(v.tabv)) - curL;
        var w = s.vol(waterL), f = s.vol(curL + waterL);
        return { main: { value: w.value, unit: w.unit, label: 'Water to add' },
          extra: [{ value: f.value, unit: f.unit, label: 'Final volume' }],
          note: 'Add water slowly and rest the spirit — dilution generates a little heat.' };
      },
    },
    {
      id: 'spirits-blend',
      name: 'Alcohol Blending Calculator',
      desc: 'Calculate final ABV when blending two liquids.',
      group: 'blend',
      need: 'The volume and ABV of each liquid you are blending.',
      accuracy: 'Assumes ideal mixing — real blends may shift very slightly.',
      next: [{ t: 'spirits-dilution', label: 'Dilute to bottling strength' }],
      fields: [
        { key: 'v1', label: 'Liquid one volume', type: 'number', dim: 'vol', default: '1' },
        { key: 'a1', label: 'Liquid one ABV', type: 'number', unit: '%', default: '60', step: '0.1' },
        { key: 'v2', label: 'Liquid two volume', type: 'number', dim: 'vol', default: '1' },
        { key: 'a2', label: 'Liquid two ABV', type: 'number', unit: '%', default: '40', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.v1) || !has(v.a1) || !has(v.v2) || !has(v.a2)) return null;
        var l1 = s.toL(v.v1), l2 = s.toL(v.v2), tot = l1 + l2;
        if (tot <= 0) return { error: 'Enter a volume greater than zero.' };
        var abv = (l1 * num(v.a1) + l2 * num(v.a2)) / tot;
        var f = s.vol(tot);
        return { main: { value: d(abv, 1), unit: '% ABV', label: 'Final ABV' },
          extra: [{ value: f.value, unit: f.unit, label: 'Final volume' }],
          note: 'Assumes ideal mixing — real blends may shift very slightly.' };
      },
    },
    {
      id: 'spirits-wash',
      name: 'Wash ABV Calculator',
      desc: 'Calculate alcohol percentage in a fermented wash.',
      group: 'plan', tag: 'Start here',
      need: 'Your hydrometer readings before and after fermentation.',
      accuracy: 'This is an estimate. Actual alcohol content may vary.',
      next: [{ t: 'spirits-yield', label: 'Estimate spirit yield' }, { h: '/blogs/all/understanding-wash-gravity-readings', label: 'Understanding wash gravity readings' }],
      fields: [
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.070', step: '0.001' },
        { key: 'fg', label: 'Final Gravity', type: 'number', unit: 'SG', default: '1.000', step: '0.001' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.fg)) return null;
        if (num(v.og) < 0.9 || num(v.og) > 1.25 || num(v.fg) < 0.9 || num(v.fg) > 1.25) return { error: 'Enter a valid gravity reading.' };
        if (num(v.fg) > num(v.og)) return { error: 'Final gravity should be lower than original gravity.' };
        var abv = (num(v.og) - num(v.fg)) * 131.25;
        return { main: { value: d(abv, 1), unit: '% ABV', label: 'Estimated wash ABV' },
          note: 'The strength of the wash before distillation.' };
      },
    },
    {
      id: 'spirits-sugarwash',
      name: 'Sugar Wash Calculator',
      desc: 'Calculate sugar needed for a wash volume and target alcohol level.',
      group: 'plan', caution: CAUTION,
      need: 'Your wash volume and target ABV.',
      accuracy: 'An estimate — top up to volume after the sugar dissolves.',
      next: [{ t: 'spirits-potential', label: 'Check potential alcohol' }, { h: '/blogs/all/sugar-washes-explained', label: 'Sugar washes explained' }],
      fields: [
        { key: 'vol', label: 'Wash volume', type: 'number', dim: 'vol', default: '25' },
        { key: 'tabv', label: 'Target ABV', type: 'number', unit: '%', default: '15', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.tabv)) return null;
        var g = s.toL(v.vol) * num(v.tabv) * 16.83;
        var w = s.wt(g);
        return { main: { value: w.value, unit: w.unit, label: 'Sugar required' },
          note: 'Top up to the wash volume after the sugar has dissolved.' };
      },
    },
    {
      id: 'spirits-potential',
      name: 'Potential Alcohol Calculator',
      desc: 'Estimate potential wash alcohol.',
      group: 'plan',
      need: 'A starting reading, or total sugar and wash volume.',
      accuracy: 'Assumes fermentation to fully dry — real washes vary.',
      next: [{ t: 'spirits-yield', label: 'Estimate spirit yield' }],
      fields: [
        { key: 'mtype', label: 'Measurement type', type: 'select', default: 'sg',
          options: [{ value: 'sg', label: 'Specific Gravity' }, { value: 'brix', label: 'Brix' }, { value: 'sugar', label: 'Sugar quantity' }] },
        { key: 'reading', label: 'Reading or quantity', type: 'number', unit: '', default: '1.070', step: '0.001', help: 'SG / °Bx, or total sugar in kg.' },
        { key: 'vol', label: 'Wash volume', type: 'number', dim: 'vol', default: '25', help: 'Only needed for sugar quantity.' },
      ],
      compute: function (v, s) {
        if (!has(v.reading)) return null;
        var r = num(v.reading), pa;
        if (v.mtype === 'sg' && (r < 0.98 || r > 1.2)) return { error: 'Enter a Specific Gravity between 0.980 and 1.200.' };
        if (v.mtype === 'brix' && (r < 0 || r > 45)) return { error: 'Enter a Brix value between 0 and 45 °Bx.' };
        if (v.mtype === 'sugar' && r < 0) return { error: 'Enter a sugar quantity of zero or more.' };
        if (v.mtype === 'sg') pa = (r - 1) * 131.25;
        else if (v.mtype === 'brix') pa = r * 0.59;
        else {
          if (!has(v.vol)) return null;
          pa = (r * 1000 / s.toL(v.vol)) / 16.83;
        }
        if (pa < 0) return { error: 'Reading is below the point where sugar is present.' };
        return { main: { value: d(pa, 1), unit: '% ABV', label: 'Estimated potential alcohol' },
          note: 'The wash strength if fermented fully out.' };
      },
    },
    {
      id: 'spirits-yield',
      name: 'Theoretical Spirit Yield Calculator',
      desc: 'Estimate potential alcohol yield from wash volume and wash ABV.',
      group: 'record', tag: 'Advanced', caution: CAUTION,
      need: 'Your wash volume, wash ABV and target finished strength.',
      accuracy: 'A theoretical maximum — real cuts and losses reduce the final figure.',
      next: [{ t: 'spirits-dilution', label: 'Dilute to bottling strength' }],
      fields: [
        { key: 'vol', label: 'Wash volume', type: 'number', dim: 'vol', default: '25' },
        { key: 'washabv', label: 'Wash ABV', type: 'number', unit: '%', default: '10', step: '0.1' },
        { key: 'tabv', label: 'Target finished spirit ABV', type: 'number', unit: '%', default: '40', step: '0.1' },
        { key: 'eff', label: 'Collection efficiency (optional)', type: 'number', unit: '%', default: '90', step: '1', help: 'How much alcohol you expect to capture.' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.washabv) || !has(v.tabv)) return null;
        var eff = has(v.eff) ? num(v.eff) / 100 : 1;
        var lpa = s.toL(v.vol) * num(v.washabv) / 100 * eff;
        var finished = lpa / (num(v.tabv) / 100);
        var fv = s.vol(finished);
        return { main: { value: d(lpa, 2), unit: 'L', label: 'Litres of pure alcohol' },
          extra: [{ value: fv.value, unit: fv.unit, label: 'Finished spirit at ' + d(num(v.tabv), 0) + '%' }],
          note: 'A theoretical maximum — real cuts and losses reduce the final figure.' };
      },
    },
    {
      id: 'spirits-hydro',
      name: 'Hydrometer Temperature Correction',
      desc: 'Correct wash gravity readings.',
      group: 'plan',
      need: 'Your measured gravity, sample temperature and calibration temperature.',
      accuracy: 'For best accuracy, use a calibrated hydrometer.',
      next: [{ t: 'spirits-wash', label: 'Calculate wash ABV' }],
      fields: [
        { key: 'sg', label: 'Measured gravity', type: 'number', unit: 'SG', default: '1.045', step: '0.001' },
        { key: 'temp', label: 'Sample temperature', type: 'number', dim: 'temp', default: '30' },
        { key: 'cal', label: 'Hydrometer calibration temperature', type: 'number', dim: 'temp', default: '20', help: 'Printed on the hydrometer — usually 20 °C.' },
      ],
      compute: function (v, s) {
        if (!has(v.sg) || !has(v.temp) || !has(v.cal)) return null;
        if (num(v.sg) < 0.98 || num(v.sg) > 1.2) return { error: 'Enter a Specific Gravity between 0.980 and 1.200.' };
        var f = function (c) { var t = c * 9 / 5 + 32; return 1.00130346 - 1.34722124e-4 * t + 2.04052596e-6 * t * t - 2.32820948e-9 * t * t * t; };
        var cg = num(v.sg) * f(s.toC(v.temp)) / f(s.toC(v.cal));
        return { main: { value: d(cg, 3), unit: 'SG', label: 'Corrected gravity reading' },
          note: 'Use this corrected figure for wash ABV and yield calculations.' };
      },
    },
    {
      id: 'spirits-cuts',
      name: 'Spirit Cuts Calculator',
      desc: 'Record and summarise the cuts from a spirit run.',
      group: 'record', caution: CAUTION,
      guide: 'A simple record tool for a run. Enter the volume of each cut you collected — this summarises the split, it does not give distillation guidance.',
      need: 'The volume of each cut — heads, hearts and tails — you collected.',
      accuracy: 'A record only — no distillation guidance is implied.',
      next: [{ t: 'spirits-standard', label: 'Calculate standard drinks' }, { h: '/blogs/all/record-keeping-for-distilling-runs', label: 'Record keeping for distilling runs' }],
      fields: [
        { key: 'total', label: 'Total collected volume', type: 'number', dim: 'vol', default: '5', help: 'Optional — leave blank to sum the cuts.' },
        { key: 'heads', label: 'Heads volume', type: 'number', dim: 'vol', default: '0.3' },
        { key: 'hearts', label: 'Hearts volume', type: 'number', dim: 'vol', default: '3' },
        { key: 'tails', label: 'Tails volume', type: 'number', dim: 'vol', default: '1' },
      ],
      compute: function (v, s) {
        if (!has(v.heads) && !has(v.hearts) && !has(v.tails)) return null;
        var h = has(v.heads) ? s.toL(v.heads) : 0, he = has(v.hearts) ? s.toL(v.hearts) : 0, t = has(v.tails) ? s.toL(v.tails) : 0;
        var sum = h + he + t;
        if (sum <= 0) return { error: 'Enter at least one cut volume greater than zero.' };
        if (has(v.total) && sum > s.toL(v.total) + 1e-6) return { error: 'Heads, hearts and tails cannot total more than the collected volume. Enter values that add up to the total collection.' };
        var tv = s.vol(sum);
        var pct = function (x) { return d(x / sum * 100, 0) + '%'; };
        return { main: { value: pct(he), unit: 'hearts', label: 'Hearts yield of run' },
          extra: [
            { value: pct(h), unit: 'heads', label: 'Heads' },
            { value: pct(t), unit: 'tails', label: 'Tails' },
            { value: tv.value + ' ' + tv.unit, unit: '', label: 'Total cuts recorded' },
          ],
          note: 'A record of how the run split — no distillation advice implied.' };
      },
    },
    {
      id: 'spirits-liqueur',
      name: 'Liqueur ABV Calculator',
      desc: 'Calculate final ABV after adding non-alcoholic liquid.',
      group: 'blend',
      need: 'Your spirit volume and ABV, and the mixer or syrup volume.',
      accuracy: 'Sugar adds a little volume — treat this as a close estimate.',
      next: [{ t: 'spirits-standard', label: 'Calculate standard drinks' }],
      fields: [
        { key: 'vol', label: 'Spirit volume', type: 'number', dim: 'vol', default: '0.7' },
        { key: 'abv', label: 'Spirit ABV', type: 'number', unit: '%', default: '40', step: '0.1' },
        { key: 'mixer', label: 'Syrup, water or mixer volume', type: 'number', dim: 'vol', default: '0.5' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.abv) || !has(v.mixer)) return null;
        var sp = s.toL(v.vol), mx = s.toL(v.mixer), tot = sp + mx;
        if (tot <= 0) return { error: 'Enter a volume greater than zero.' };
        var abv = sp * num(v.abv) / tot;
        var f = s.vol(tot);
        return { main: { value: d(abv, 1), unit: '% ABV', label: 'Final ABV' },
          extra: [{ value: f.value, unit: f.unit, label: 'Final volume' }],
          note: 'Sugar in syrup adds a little volume — treat this as a close estimate.' };
      },
    },
    {
      id: 'spirits-standard',
      name: 'Standard Drinks Calculator',
      desc: 'Calculate standard drinks per bottle, batch or serving.',
      group: 'serve',
      guide: 'Uses the Australian standard drink — 10 g of pure alcohol. Handy for labelling and responsible serving.',
      need: 'The volume and ABV, and optionally a serving size.',
      accuracy: 'Standard drink calculations use the Australian standard of 10 g alcohol.',
      next: [{ t: 'spirits-bottle', label: 'Estimate bottle yield' }, { h: '/blogs/all/how-standard-drinks-are-calculated', label: 'How standard drinks are calculated' }],
      fields: [
        { key: 'vol', label: 'Volume', type: 'number', dim: 'vol', default: '0.7' },
        { key: 'abv', label: 'ABV', type: 'number', unit: '%', default: '40', step: '0.1' },
        { key: 'serving', label: 'Serving size', type: 'number', unit: 'mL', default: '30' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.abv)) return null;
        var mL = s.toL(v.vol) * 1000, abv = num(v.abv);
        var std = mL * abv / 100 * 0.789 / 10;
        var out = { main: { value: d(std, 1), unit: std === 1 ? 'std drink' : 'std drinks', label: 'Total standard drinks' },
          note: 'Australian standard drink = 10 g of pure alcohol.' };
        if (has(v.serving)) {
          var per = num(v.serving) * abv / 100 * 0.789 / 10;
          out.extra = [{ value: d(per, 1), unit: 'per serve', label: 'Per ' + d(num(v.serving), 0) + ' mL serving' }];
        }
        return out;
      },
    },
    {
      id: 'spirits-bottle',
      name: 'Bottle Yield Calculator',
      desc: 'Calculate number of bottles from a finished spirit batch.',
      group: 'serve', tag: 'Packaging',
      need: 'Your total packaged volume and chosen bottle size.',
      next: [{ h: '/collections/spirits-distillation-1', label: 'Explore spirits-making resources' }],
      fields: [
        { key: 'vol', label: 'Total packaged volume', type: 'number', dim: 'vol', default: '5' },
        { key: 'size', label: 'Bottle size', type: 'select', default: '700',
          options: [{ value: '375', label: '375 mL' }, { value: '500', label: '500 mL' }, { value: '700', label: '700 mL' }, { value: '750', label: '750 mL' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol)) return null;
        if (num(v.vol) <= 0) return { error: 'Total packaged volume must be greater than zero.' };
        if (num(v.size) <= 0) return { error: 'Bottle size must be greater than zero.' };
        var mL = s.toL(v.vol) * 1000, bottles = Math.ceil(mL / num(v.size));
        return { main: { value: String(bottles), unit: 'bottles', label: 'Bottles required' },
          note: 'Rounded up — the last bottle may be partly filled.' };
      },
    },
    {
      id: 'spirits-convert',
      name: 'Alcohol Unit Converter',
      desc: 'Convert between common alcohol measurements.',
      group: 'blend',
      need: 'A value in ABV, proof, or pure-alcohol volume.',
      next: [{ t: 'spirits-standard', label: 'Calculate standard drinks' }],
      fields: [
        { key: 'type', label: 'Input type', type: 'select', default: 'abv',
          options: [{ value: 'abv', label: 'ABV (%)' }, { value: 'proof', label: 'US proof' }, { value: 'lpa', label: 'Litres of pure alcohol' }, { value: 'ml', label: 'mL of pure alcohol' }] },
        { key: 'value', label: 'Value', type: 'number', unit: '', default: '40', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.value)) return null;
        var x = num(v.value);
        if (x < 0) return { error: 'Enter a value of zero or more.' };
        if (v.type === 'abv' && x > 100) return { error: 'ABV cannot exceed 100%.' };
        if (v.type === 'proof' && x > 200) return { error: 'US proof cannot exceed 200.' };
        if (v.type === 'abv' || v.type === 'proof') {
          var abv = v.type === 'abv' ? x : x / 2;
          return { main: { value: d(abv, 1), unit: '% ABV', label: 'Alcohol by volume' },
            extra: [
              { value: d(abv * 2, 0), unit: 'proof', label: 'US proof' },
              { value: d(abv * 10, 0), unit: 'mL/L', label: 'Pure alcohol per litre' },
            ],
            note: 'Strength measures. Pure-alcohol volume depends on total liquid volume.' };
        }
        var mL = v.type === 'lpa' ? x * 1000 : x;
        return { main: { value: d(mL, 0), unit: 'mL', label: 'Millilitres of pure alcohol' },
          extra: [{ value: d(mL / 1000, 3), unit: 'L', label: 'Litres of pure alcohol' }],
          note: 'Volume of pure ethanol, independent of the strength it is diluted to.' };
      },
    },
  ];

  var CFG = {
    page: 'spirits',
    units: 'metric',
    // Group order defines the continuous 01–12 numbering (Plan → Record → Blend → Serve)
    groups: [
      { id: 'plan', anchor: 'grp-plan', title: 'Plan your wash', learn: [
        { title: 'Understanding wash gravity readings', kicker: 'Guide', href: '/blogs/all/understanding-wash-gravity-readings' },
        { title: 'Sugar washes explained', kicker: 'Guide', href: '/blogs/all/sugar-washes-explained' },
      ] },
      { id: 'record', anchor: 'grp-record', title: 'Estimate and record', learn: [
        { title: 'Record keeping for distilling runs', kicker: 'Guide', href: '/blogs/all/record-keeping-for-distilling-runs' },
        { title: 'Making cuts: heads, hearts and tails', kicker: 'Guide', href: '/blogs/all/making-cuts-heads-hearts-and-tails' },
      ] },
      { id: 'blend', anchor: 'grp-blend', title: 'Blend and dilute', learn: [
        { title: 'Understanding alcohol dilution', kicker: 'Guide', href: '/blogs/all/understanding-alcohol-dilution' },
        { title: 'Blending spirits and liqueurs', kicker: 'Guide', href: '/blogs/all/blending-spirits-and-liqueurs' },
      ] },
      { id: 'serve', anchor: 'grp-serve', title: 'Bottle and serve', learn: [
        { title: 'How standard drinks are calculated', kicker: 'Guide', href: '/blogs/all/how-standard-drinks-are-calculated' },
        { title: 'Bottling and labelling your spirit', kicker: 'Guide', href: '/blogs/all/bottling-and-labelling-your-spirit' },
      ] },
    ],
    chips: [
      { label: 'Measure wash strength', id: 'spirits-wash' },
      { label: 'Estimate yield', id: 'spirits-yield' },
      { label: 'Dilute spirit', id: 'spirits-dilution' },
      { label: 'Blend alcohol', id: 'spirits-blend' },
      { label: 'Bottle a batch', id: 'spirits-bottle' },
    ],
    footer: {
      responsible: 'Know your strength before you pour. Standard drink figures are estimates, especially for homemade beverages. In Australia, one standard drink contains 10 g of pure alcohol. Enjoy what you make, share responsibly and never drink and drive.',
      related: [
        { label: 'Explore spirits-making resources', href: '/collections/spirits-distillation-1' },
        { label: 'View measuring and testing equipment', href: '/collections/hydrometers-refractometers' },
      ],
    },
    calculators: spirits,
  };

  if (window.TABCalc && window.TABCalc.register) window.TABCalc.register('spirits', CFG);
  else { window.TABCalc = window.TABCalc || {}; (window.TABCalc._pending = window.TABCalc._pending || []).push(['spirits', CFG]); }
})();
